/**
 * Phase 8 slice FE — PI Sign Visit + Reopen + Sign-off History drawer.
 *
 * Renders on the VisitDetailPage. The panel:
 *   - shows the current lock state pill (Unlocked / Signed – Pending SDV /
 *     Locked) with signatory + timestamp (extended-contract AC5);
 *   - exposes "Sign Visit" (AC1, AC2, AC9) to PI / sub-investigator /
 *     site-admin (button hidden for other roles per AC10);
 *   - exposes "Reopen Visit" (AC11) when the visit is signed_pending_sdv
 *     or locked (AC11);
 *   - opens the full sign-off history drawer showing every signature +
 *     reopen ceremony (AC13).
 *
 * The re-authentication ceremony (AC2/AC12) is stubbed pending the
 * /auth/re-authenticate handler — the modal accepts a receipt string
 * so this surface plugs in immediately when the auth work lands.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  History,
  Loader2,
  Lock,
  PenLine,
  ShieldAlert,
  Unlock,
  X,
} from 'lucide-react';
import {
  authReAuth,
  visitLifecycle,
  type VisitLockState,
  type VisitReAuthMethod,
  type VisitSignReadiness,
} from '@/api/client';
import { useHasRole } from '@/components/RoleGate';
import { fmtDateTime } from '@/lib/dates';
import { errorText } from '@/lib/permissionError';

const SIGN_ROLES = [
  'principal_investigator',
  'sub_investigator',
  'site_admin',
  'platform_admin',
  'application_admin',
] as const;

const LOCK_LABEL: Record<VisitLockState, string> = {
  unlocked: 'Unlocked',
  signed_pending_sdv: 'Signed — Pending SDV',
  locked: 'Locked',
};

const LOCK_BADGE: Record<VisitLockState, string> = {
  unlocked: 'bg-slate-100 text-slate-700 border-slate-200',
  signed_pending_sdv: 'bg-amber-50 text-amber-700 border-amber-200',
  locked: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const DEFAULT_SIGNATURE_MEANING =
  'By signing you attest that visit data has been reviewed and is accurate.';

export function VisitLifecyclePanel({ visitId }: { visitId: string }) {
  const canSign = useHasRole(SIGN_ROLES);
  const qc = useQueryClient();

  const historyQ = useQuery({
    queryKey: ['visit', 'sign-history', visitId],
    queryFn: () => visitLifecycle.signHistory(visitId),
  });

  const [signOpen, setSignOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const state = historyQ.data?.lock_state ?? 'unlocked';
  const latestSig = historyQ.data?.signatures[0];
  // Visit sign-off flow (14 Oct 2026): Sign Visit appears only once the visit
  // date is recorded and every required form is submitted — the same moment it
  // lands in the PI's eSignature queue. If readiness couldn't be worked out,
  // the button stays and the server enforces the rule.
  const readiness = state === 'unlocked' ? historyQ.data?.sign_readiness ?? null : null;
  const signReady = readiness ? readiness.ready : true;

  if (historyQ.isLoading) {
    return (
      <div className="card p-4 text-center text-xs text-ink-muted">
        <Loader2 className="mx-auto h-4 w-4 animate-spin" />
      </div>
    );
  }

  return (
    <section className="card p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px] font-medium ${LOCK_BADGE[state]}`}
            >
              {state === 'unlocked' ? (
                <Unlock className="h-3 w-3" />
              ) : (
                <Lock className="h-3 w-3" />
              )}
              {LOCK_LABEL[state]}
            </span>
            {latestSig && (
              <span className="text-[10px] text-ink-muted">
                signed v{latestSig.signed_visit_version} · {fmtDateTime(latestSig.signed_at)}
              </span>
            )}
          </div>
          {latestSig && (
            <p className="mt-1 text-[10px] text-ink-muted">
              <span className="font-mono">{latestSig.reference_number}</span> ·{' '}
              {latestSig.signature_meaning}
            </p>
          )}
          {readiness && <SignReadinessLine readiness={readiness} />}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className="rounded border border-surface-border px-2 py-1 text-[11px] font-medium hover:bg-surface-muted/50"
          >
            <History className="mr-1 inline h-3 w-3" />
            History
          </button>
          {canSign && state === 'unlocked' && signReady && (
            <button
              type="button"
              onClick={() => setSignOpen(true)}
              className="rounded bg-accent-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-accent-700"
            >
              <PenLine className="mr-1 inline h-3 w-3" />
              Sign Visit
            </button>
          )}
          {canSign && state !== 'unlocked' && (
            <button
              type="button"
              onClick={() => setReopenOpen(true)}
              className="rounded border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-100"
            >
              <ShieldAlert className="mr-1 inline h-3 w-3" />
              Reopen Visit
            </button>
          )}
        </div>
      </div>

      {signOpen && (
        <SignVisitModal
          visitId={visitId}
          onClose={() => setSignOpen(false)}
          onSigned={() => {
            setSignOpen(false);
            qc.invalidateQueries({ queryKey: ['visit', 'sign-history', visitId] });
            qc.invalidateQueries({ queryKey: ['visit', visitId] });
          }}
        />
      )}
      {reopenOpen && (
        <ReopenVisitModal
          visitId={visitId}
          onClose={() => setReopenOpen(false)}
          onReopened={() => {
            setReopenOpen(false);
            qc.invalidateQueries({ queryKey: ['visit', 'sign-history', visitId] });
            qc.invalidateQueries({ queryKey: ['visit', visitId] });
          }}
        />
      )}
      {historyOpen && historyQ.data && (
        <SignHistoryDrawer
          data={historyQ.data}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </section>
  );
}

/** Progress toward PI signature: visit date recorded + forms submitted. */
function SignReadinessLine({ readiness: r }: { readiness: VisitSignReadiness }) {
  const formsDone = r.forms_submitted === r.forms_required;
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[10px]">
      <span className={`rounded border px-1.5 py-0.5 ${r.date_recorded ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-surface-border text-ink-muted'}`}>
        {r.date_recorded ? 'Visit date recorded' : 'Visit date not recorded'}
      </span>
      {r.forms_required > 0 && (
        <span className={`rounded border px-1.5 py-0.5 ${formsDone ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-surface-border text-ink-muted'}`}>
          Forms {r.forms_submitted} of {r.forms_required} submitted
        </span>
      )}
      <span className={r.ready ? 'font-medium text-accent-700' : 'text-ink-muted'}>
        {r.ready ? 'Ready for PI signature' : 'Not ready for PI signature yet'}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AC2 — sign modal with re-auth ceremony (stubbed for now — receipt is
// accepted as an opaque string).

export function SignVisitModal({
  visitId,
  onClose,
  onSigned,
}: {
  visitId: string;
  onClose: () => void;
  onSigned: () => void;
}) {
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [meaning, setMeaning] = useState(DEFAULT_SIGNATURE_MEANING);

  const sign = useMutation({
    mutationFn: async () => {
      // Extended-contract AC2 — call /auth/re-authenticate first,
      // then present the receipt to the sign endpoint. The BE
      // verifies the receipt is bound to the caller, unexpired,
      // purpose='sign_visit', and method matches.
      const receipt = await authReAuth.reAuthenticate({
        password,
        totp_code: otp.trim() || undefined,
        purpose: 'sign_visit',
      });
      return visitLifecycle.sign(visitId, {
        re_auth_receipt: receipt.receipt,
        re_auth_method: receipt.method as VisitReAuthMethod,
        signature_meaning: meaning.trim(),
      });
    },
    onSuccess: (r) => {
      toast.success(`Signed as v${r.signed_visit_version}.`);
      onSigned();
    },
    onError: (e) => toast.error(errorText(e, 'Sign failed')),
  });

  const canSubmit = password.length >= 4 && meaning.trim().length >= 4 && !sign.isPending;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-md rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-start justify-between border-b border-surface-border px-4 py-3">
          <h3 className="text-sm font-semibold text-ink-primary">
            <PenLine className="mr-1 inline h-4 w-4 text-brand-500" />
            Sign Visit — 21 CFR Part 11 e-signature
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="space-y-3 px-4 py-3 text-xs">
          <label className="block">
            <span className="font-medium text-ink-secondary">Password *</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="mt-1 w-full rounded border border-surface-border px-2 py-1"
            />
          </label>
          <label className="block">
            <span className="font-medium text-ink-secondary">
              OTP (if TOTP-enrolled)
            </span>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              maxLength={6}
              className="mt-1 w-full rounded border border-surface-border px-2 py-1 font-mono"
            />
          </label>
          <label className="block">
            <span className="font-medium text-ink-secondary">Signature meaning *</span>
            <textarea
              value={meaning}
              onChange={(e) => setMeaning(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded border border-surface-border px-2 py-1"
            />
          </label>
          <p className="rounded border border-emerald-200 bg-emerald-50 p-2 text-[10px] text-emerald-800">
            21 CFR Part 11 re-authentication is enforced — the server verifies
            your password (and TOTP if enrolled) before recording the signature.
          </p>
        </div>
        <footer className="flex items-center justify-end gap-2 border-t border-surface-border px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => sign.mutate()}
            disabled={!canSubmit}
            className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {sign.isPending ? 'Signing…' : 'Sign Visit'}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ReopenVisitModal({
  visitId,
  onClose,
  onReopened,
}: {
  visitId: string;
  onClose: () => void;
  onReopened: () => void;
}) {
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [reason, setReason] = useState('');

  const reopen = useMutation({
    mutationFn: async () => {
      // Extended-contract AC12 — re-auth ceremony precedes reopen.
      const receipt = await authReAuth.reAuthenticate({
        password,
        totp_code: otp.trim() || undefined,
        purpose: 'reopen_visit',
      });
      return visitLifecycle.reopen(visitId, {
        re_auth_receipt: receipt.receipt,
        re_auth_method: receipt.method as VisitReAuthMethod,
        reason_text: reason.trim(),
      });
    },
    onSuccess: () => {
      toast.success('Visit reopened.');
      onReopened();
    },
    onError: (e) => toast.error(errorText(e, 'Reopen failed')),
  });

  const canSubmit = password.length >= 4 && reason.trim().length >= 20 && !reopen.isPending;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-md rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-start justify-between border-b border-surface-border px-4 py-3">
          <h3 className="text-sm font-semibold text-ink-primary">
            <ShieldAlert className="mr-1 inline h-4 w-4 text-amber-600" />
            Reopen Visit
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="space-y-3 px-4 py-3 text-xs">
          <p className="rounded border border-amber-200 bg-amber-50 p-2 text-[10px] text-amber-800">
            Reopening releases the current signature lock. After edits, the visit
            must be re-signed. Both signatures stay in the history.
          </p>
          <label className="block">
            <span className="font-medium text-ink-secondary">Password *</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="mt-1 w-full rounded border border-surface-border px-2 py-1"
            />
          </label>
          <label className="block">
            <span className="font-medium text-ink-secondary">
              OTP (if TOTP-enrolled)
            </span>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              maxLength={6}
              className="mt-1 w-full rounded border border-surface-border px-2 py-1 font-mono"
            />
          </label>
          <label className="block">
            <span className="font-medium text-ink-secondary">
              Reason for reopen * <span className="text-ink-muted">(min 20 chars)</span>
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded border border-surface-border px-2 py-1"
            />
            <span className="mt-0.5 block text-[10px] text-ink-muted">
              {reason.trim().length}/20 chars
            </span>
          </label>
        </div>
        <footer className="flex items-center justify-end gap-2 border-t border-surface-border px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => reopen.mutate()}
            disabled={!canSubmit}
            className="rounded bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {reopen.isPending ? 'Reopening…' : 'Reopen Visit'}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function SignHistoryDrawer({
  data,
  onClose,
}: {
  data: NonNullable<ReturnType<typeof visitLifecycle.signHistory> extends Promise<infer T> ? T : never>;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-y-0 right-0 z-[90] flex w-full max-w-md flex-col border-l border-surface-border bg-surface-card shadow-xl">
      <header className="flex items-center justify-between border-b border-surface-border px-4 py-3">
        <h3 className="text-sm font-semibold text-ink-primary">
          <History className="mr-1 inline h-4 w-4 text-brand-500" />
          Sign-off history
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
        >
          <X className="h-4 w-4" />
        </button>
      </header>
      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        <section>
          <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-ink-muted">
            Signatures ({data.signatures.length})
          </h4>
          {data.signatures.length === 0 ? (
            <p className="text-[11px] text-ink-muted">No signatures yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {data.signatures.map((s) => (
                <li
                  key={s.signature_id}
                  className="rounded border border-emerald-200 bg-emerald-50/40 p-2 text-[11px]"
                >
                  <div className="font-medium text-emerald-800">
                    v{s.signed_visit_version} · {s.reference_number}
                  </div>
                  <div className="text-[10px] font-medium text-ink-primary">
                    Signed by {s.signed_by_name ?? 'Unknown signer'}
                  </div>
                  <div className="text-[10px] text-ink-muted">
                    Signed {fmtDateTime(s.signed_at)} · Re-authenticated by{' '}
                    {s.re_auth_method === 'password_otp' ? 'password + one-time code' : 'password'}
                  </div>
                  <div className="mt-1 text-[10px]">{s.signature_meaning}</div>
                  {s.supersedes_signature_id && (
                    <div className="mt-1 text-[10px] text-ink-muted">
                      supersedes {s.supersedes_signature_id.slice(0, 8)}…
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
        <section>
          <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-ink-muted">
            Reopens ({data.reopens.length})
          </h4>
          {data.reopens.length === 0 ? (
            <p className="text-[11px] text-ink-muted">No reopens.</p>
          ) : (
            <ul className="space-y-1.5">
              {data.reopens.map((r) => (
                <li
                  key={r.reopen_id}
                  className="rounded border border-amber-200 bg-amber-50/40 p-2 text-[11px]"
                >
                  <div className="font-medium text-amber-800">
                    {r.reference_number}
                  </div>
                  <div className="text-[10px] font-medium text-ink-primary">
                    Reopened by {r.reopened_by_name ?? 'Unknown user'}
                  </div>
                  <div className="text-[10px] text-ink-muted">
                    Reopened {fmtDateTime(r.reopened_at)} · Re-authenticated by{' '}
                    {r.re_auth_method === 'password_otp' ? 'password + one-time code' : 'password'}
                  </div>
                  <div className="mt-1 text-[10px]">{r.reason_text}</div>
                  <div className="mt-1 text-[10px] text-ink-muted">
                    prior signature {r.prior_signature_id.slice(0, 8)}…
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
